import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { LogIn, Eye, EyeOff } from "lucide-react";
import { useStore } from "@/context/StoreContext";
import { formatApiError } from "@/lib/api";
import { toast } from "sonner";

export default function Login() {
  const { login } = useStore();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [show, setShow] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const user = await login(email, password);
      toast.success(`Welcome back, ${user.name.split(" ")[0]}!`);
      if (["admin", "staff"].includes(user.role)) navigate("/admin");
      else if (user.role === "vendor") navigate("/vendor/dashboard");
      else navigate("/account");
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-[70vh] grid lg:grid-cols-2">
      <div className="hidden lg:block relative bg-secondary">
        <div className="absolute inset-0 braj-pattern opacity-40" />
        <div className="relative h-full flex flex-col justify-center p-14 text-white">
          <div className="h-12 w-12 rounded-xl bg-white/15 grid place-items-center font-serif font-bold text-xl mb-6">Sv</div>
          <h2 className="text-4xl font-serif font-bold leading-tight">Welcome back to<br />Shri Vrindavan Shop</h2>
          <p className="mt-4 text-white/80 max-w-sm">Braj Ki Bhakti, Aapke Ghar Tak. Sign in to continue your spiritual shopping journey.</p>
        </div>
      </div>

      <div className="flex items-center justify-center p-6 sm:p-12">
        <form onSubmit={submit} className="w-full max-w-sm" data-testid="login-form">
          <h1 className="text-3xl font-serif font-bold text-stone-900">Sign In</h1>
          <p className="mt-1 text-sm text-stone-500">Enter your credentials to access your account.</p>

          {error && <div data-testid="login-error" className="mt-4 rounded-lg bg-red-50 border border-red-100 text-red-700 text-sm px-4 py-2.5">{error}</div>}

          <label className="block mt-6 text-sm">
            <span className="text-stone-600">Email</span>
            <input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} data-testid="login-email" className="mt-1 w-full rounded-lg border border-border px-3 py-2.5 outline-none focus:ring-2 focus:ring-primary/30" />
          </label>
          <label className="block mt-4 text-sm">
            <span className="text-stone-600">Password</span>
            <div className="mt-1 flex items-center rounded-lg border border-border focus-within:ring-2 focus-within:ring-primary/30">
              <input type={show ? "text" : "password"} required value={password} onChange={(e) => setPassword(e.target.value)} data-testid="login-password" className="flex-1 px-3 py-2.5 outline-none bg-transparent" />
              <button type="button" onClick={() => setShow((s) => !s)} className="px-3 text-stone-400">{show ? <EyeOff size={16} /> : <Eye size={16} />}</button>
            </div>
          </label>

          <button disabled={loading} data-testid="login-submit" className="mt-6 w-full inline-flex items-center justify-center gap-2 rounded-full bg-primary text-primary-foreground font-semibold py-3 hover:bg-[hsl(28_90%_38%)] disabled:opacity-60">
            <LogIn size={17} /> {loading ? "Signing in..." : "Sign In"}
          </button>

          <p className="mt-5 text-center text-sm text-stone-500">
            <Link to="/forgot-password" data-testid="login-forgot-link" className="font-semibold text-secondary">Forgot password?</Link> · New here? <Link to="/register" className="font-semibold text-secondary">Create an account</Link>
          </p>
        </form>
      </div>
    </div>
  );
}
